import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plug, Webhook, CheckCircle2, XCircle, Loader2, Trash2, RefreshCw, Copy } from "lucide-react";
import {
  registerShopierWebhook,
  deleteShopierWebhook,
  testShopierConnection,
} from "@/lib/shopier.functions";

export const Route = createFileRoute("/harikaradmin/shopier")({
  head: () => ({ meta: [{ title: "Shopier — JUËN Admin" }] }),
  component: AdminShopierPage,
});

interface WebhookRow {
  id: string;
  shopier_webhook_id: string;
  event: string;
  url: string;
  is_active: boolean;
  created_at: string;
}

interface EventLogRow {
  id: string;
  event: string;
  shopier_order_id: string | null;
  signature_valid: boolean;
  match_status: string | null;
  error: string | null;
  created_at: string;
  payload?: any;
}

interface ProfileLite {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  points_balance: number | null;
}

function buyerInfo(payload: any) {
  const p = payload ?? {};
  const email = p.email ?? p.buyer_email ?? p.customer_email ?? null;
  const name =
    [p.buyername, p.buyersurname].filter(Boolean).join(" ").trim() ||
    [p.buyer_name, p.buyer_surname].filter(Boolean).join(" ").trim() ||
    p.fullname ||
    p.name ||
    null;
  const phone = p.buyer_phone ?? p.phone ?? p.gsm ?? null;
  const note = p.customernote ?? p.buyer_note ?? p.note ?? null;
  const priceRaw = p.price ?? p.total_amount ?? p.total ?? null;
  const priceTl = priceRaw ? parseFloat(String(priceRaw).replace(",", ".")) : null;
  return { email, name, phone, note, priceTl };
}


const WEBHOOK_EVENTS = [
  { value: "order.created", label: "Sipariş oluşturuldu (ödendi)" },
  { value: "order.fulfilled", label: "Sipariş tamamlandı" },
  { value: "refund.requested", label: "İade talep edildi" },
  { value: "refund.updated", label: "İade güncellendi" },
] as const;

function AdminShopierPage() {
  const { user, isAdmin, loading, session } = useAuth();
  const navigate = useNavigate();
  const [hooks, setHooks] = useState<WebhookRow[]>([]);
  const [logs, setLogs] = useState<EventLogRow[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [testing, setTesting] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [event, setEvent] = useState<string>("order.created");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [unmatched, setUnmatched] = useState<EventLogRow[]>([]);
  const [openLogId, setOpenLogId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<ProfileLite[]>([]);
  const [selected, setSelected] = useState<ProfileLite | null>(null);
  const [pointsInput, setPointsInput] = useState("");
  const [resolving, setResolving] = useState(false);

  const testFn = useServerFn(testShopierConnection);
  const registerFn = useServerFn(registerShopierWebhook);
  const deleteFn = useServerFn(deleteShopierWebhook);

  useEffect(() => {
    if (!loading) {
      if (!user) void navigate({ to: "/login" });
      else if (!isAdmin) void navigate({ to: "/dashboard" });
    }
  }, [loading, user, isAdmin, navigate]);

  useEffect(() => {
    if (!webhookUrl) {
      // Always use the stable published URL — Shopier must be able to reach it
      setWebhookUrl("https://juen.club/api/public/shopier-webhook");
    }
  }, [webhookUrl]);

  const load = useCallback(async () => {
    setLoadingList(true);
    const [{ data: h }, { data: l }, { data: u }] = await Promise.all([
      supabase
        .from("shopier_webhooks")
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false }),
      supabase
        .from("shopier_event_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50),
      supabase
        .from("shopier_event_log")
        .select("*")
        .in("match_status", ["no_match", "invalid_signature"])
        .order("created_at", { ascending: false })
        .limit(100),
    ]);
    setHooks((h as WebhookRow[]) ?? []);
    setLogs((l as EventLogRow[]) ?? []);
    setUnmatched(((u as EventLogRow[]) ?? []).filter((r) => buyerInfo(r.payload).priceTl));
    setLoadingList(false);
  }, []);

  const openResolver = (log: EventLogRow) => {
    const info = buyerInfo(log.payload);
    setOpenLogId(log.id);
    setSelected(null);
    setResults([]);
    setPointsInput(info.priceTl ? String(Math.round(info.priceTl)) : "");
    setSearch(info.email || info.name || "");
    void searchMembers(info.email || info.name || "");
  };

  const searchMembers = async (q: string) => {
    const term = q.trim();
    if (term.length < 3) {
      setResults([]);
      return;
    }
    const { data } = await supabase
      .from("profiles")
      .select("id,first_name,last_name,email,phone,points_balance")
      .or(
        `email.ilike.%${term}%,first_name.ilike.%${term}%,last_name.ilike.%${term}%,phone.ilike.%${term}%`,
      )
      .limit(10);
    setResults((data as ProfileLite[]) ?? []);
  };

  const handleResolve = async (logId: string) => {
    if (!selected) {
      toast.error("Üye seç");
      return;
    }
    const pts = parseInt(pointsInput, 10);
    if (!pts || pts <= 0) {
      toast.error("Geçerli J miktarı gir");
      return;
    }
    setResolving(true);
    try {
      const { error } = await supabase.rpc("shopier_admin_resolve_log", {
        _log_id: logId,
        _user_id: selected.id,
        _points: pts,
      } as never);
      if (error) throw error;
      toast.success(`${pts} J yüklendi ✓`);
      setOpenLogId(null);
      setSelected(null);
      void load();
    } catch (e: any) {
      toast.error(e?.message ?? "İşlem başarısız");
    } finally {
      setResolving(false);
    }
  };


  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">
          Yükleniyor...
        </div>
      </div>
    );
  }

  const authHeaders = () =>
    session?.access_token ? { authorization: `Bearer ${session.access_token}` } : undefined;

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await testFn({ headers: authHeaders() } as any);
      if (res.success) toast.success("Shopier bağlantısı çalışıyor ✓");
      else toast.error(`Hata ${res.status}: ${res.message}`);
    } catch (e: any) {
      toast.error(e?.message ?? "Bağlantı hatası");
    } finally {
      setTesting(false);
    }
  };

  const handleRegister = async () => {
    if (!webhookUrl) {
      toast.error("Webhook URL gerekli");
      return;
    }
    setRegistering(true);
    try {
      const res = await registerFn({ data: { event: event as any, url: webhookUrl }, headers: authHeaders() } as any);
      if (res.success) toast.success("Webhook kaydedildi ✓");
      else toast.warning(res.message ?? "Shopier API yanıt vermedi; URL'yi panelden manuel ekleyin.");
      void load();
    } catch (e: any) {
      toast.error(e?.message ?? "Kayıt hatası");
    } finally {
      setRegistering(false);
    }
  };

  const handleDelete = async (shopierId: string) => {
    if (!confirm("Bu webhook'u silmek istediğine emin misin?")) return;
    try {
      await deleteFn({ data: { shopier_webhook_id: shopierId }, headers: authHeaders() } as any);
      toast.success("Webhook silindi");
      void load();
    } catch (e: any) {
      toast.error(e?.message ?? "Silme hatası");
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success("URL kopyalandı");
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Shopier Entegrasyonu</h1>
          <p className="mt-2 text-muted-foreground">
            Otomatik ödeme onayı, sipariş eşleştirme ve webhook yönetimi.
          </p>
        </div>

        <AdminTabs />

        {/* Connection test */}
        <div className="mb-6 rounded-2xl border border-border bg-card p-5 shadow-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <Plug className="h-5 w-5" /> API Bağlantısı
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Shopier Personal Access Token'ın çalıştığını doğrula.
              </p>
            </div>
            <Button onClick={handleTest} disabled={testing} variant="outline">
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Test Et"}
            </Button>
          </div>
        </div>

        {/* Register webhook */}
        <div className="mb-6 rounded-2xl border border-border bg-card p-5 shadow-card">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Webhook className="h-5 w-5" /> Webhook Ekle
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Shopier'de bir ödeme tamamlandığında bu URL'ye anlık bildirim gelir ve sipariş otomatik onaylanır.
          </p>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="event">Olay türü</Label>
              <select
                id="event"
                value={event}
                onChange={(e) => setEvent(e.target.value)}
                className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {WEBHOOK_EVENTS.map((e) => (
                  <option key={e.value} value={e.value}>
                    {e.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="url">Bildirim URL'si</Label>
              <div className="mt-1.5 flex gap-2">
                <Input
                  id="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://.../api/public/shopier-webhook"
                />
                <Button onClick={copyUrl} variant="outline" size="icon" type="button">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <Button onClick={handleRegister} disabled={registering} className="mt-4">
            {registering ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Webhook'u Kaydet
          </Button>
        </div>

        {/* Active webhooks */}
        <div className="mb-6 rounded-2xl border border-border bg-card p-5 shadow-card">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Aktif Webhook'lar</h2>
            <Button onClick={() => load()} variant="ghost" size="sm">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          {loadingList ? (
            <p className="mt-3 text-sm text-muted-foreground">Yükleniyor...</p>
          ) : hooks.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">Henüz aktif webhook yok.</p>
          ) : (
            <div className="mt-3 space-y-2">
              {hooks.map((h) => (
                <div
                  key={h.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-border p-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{h.event}</div>
                    <div className="truncate text-xs text-muted-foreground">{h.url}</div>
                  </div>
                  <Button
                    onClick={() => handleDelete(h.shopier_webhook_id)}
                    variant="ghost"
                    size="icon"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Unmatched payments */}
        <div className="mb-6 rounded-2xl border border-warning/40 bg-card p-5 shadow-card">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Eşleşmeyen Ödemeler</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Shopier'den ödeme geldi ama üyeye bağlanamadı. Buradan üyeyi seçip J'yi tek tıkla yükle.
              </p>
            </div>
            <Button onClick={() => load()} variant="ghost" size="sm">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {loadingList ? (
            <p className="mt-3 text-sm text-muted-foreground">Yükleniyor...</p>
          ) : unmatched.length === 0 ? (
            <p className="mt-3 text-sm text-success">Bekleyen eşleşmeyen ödeme yok ✓</p>
          ) : (
            <div className="mt-3 space-y-2">
              {unmatched.map((l) => {
                const info = buyerInfo(l.payload);
                const isOpen = openLogId === l.id;
                return (
                  <div key={l.id} className="rounded-xl border border-border p-3 text-sm">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium">
                          {info.name ?? "İsimsiz"}{" "}
                          {info.priceTl != null && (
                            <span className="text-muted-foreground">
                              · {info.priceTl.toLocaleString("tr-TR")} ₺
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {info.email ?? "e-posta yok"}
                          {info.phone ? ` · ${info.phone}` : ""}
                        </div>
                        {info.note && (
                          <div className="mt-0.5 font-mono text-xs text-muted-foreground">
                            Not: {info.note}
                          </div>
                        )}
                        <div className="mt-0.5 font-mono text-xs text-muted-foreground">
                          Shopier #{l.shopier_order_id ?? "—"} ·{" "}
                          {new Date(l.created_at).toLocaleString("tr-TR")}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant={isOpen ? "outline" : "default"}
                        onClick={() => (isOpen ? setOpenLogId(null) : openResolver(l))}
                      >
                        {isOpen ? "Kapat" : "Üyeye Bağla"}
                      </Button>
                    </div>

                    {isOpen && (
                      <div className="mt-3 space-y-3 rounded-lg border border-border bg-muted/20 p-3">
                        <div>
                          <Label htmlFor={`s-${l.id}`}>Üye ara (isim, e-posta, telefon)</Label>
                          <Input
                            id={`s-${l.id}`}
                            className="mt-1.5"
                            value={search}
                            onChange={(e) => {
                              setSearch(e.target.value);
                              void searchMembers(e.target.value);
                            }}
                          />
                        </div>

                        {results.length > 0 && (
                          <div className="space-y-1">
                            {results.map((p) => (
                              <button
                                key={p.id}
                                type="button"
                                onClick={() => setSelected(p)}
                                className={`w-full rounded-lg border p-2 text-left text-sm ${
                                  selected?.id === p.id
                                    ? "border-primary bg-primary/10"
                                    : "border-border hover:bg-muted/40"
                                }`}
                              >
                                <div className="font-medium">
                                  {p.first_name} {p.last_name}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {p.email} · {p.points_balance ?? 0} J
                                </div>
                              </button>
                            ))}
                          </div>
                        )}

                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <Label htmlFor={`p-${l.id}`}>Yüklenecek J</Label>
                            <Input
                              id={`p-${l.id}`}
                              className="mt-1.5"
                              inputMode="numeric"
                              value={pointsInput}
                              onChange={(e) => setPointsInput(e.target.value.replace(/\D/g, ""))}
                            />
                          </div>
                          <Button
                            onClick={() => handleResolve(l.id)}
                            disabled={resolving || !selected}
                          >
                            {resolving ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : null}
                            J Yükle ve Kapat
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Event log */}

        <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
          <h2 className="text-lg font-semibold">Son Olaylar</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Shopier'den gelen son 50 webhook bildirimi.
          </p>
          {logs.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">Henüz olay yok.</p>
          ) : (
            <div className="mt-3 space-y-2">
              {logs.map((l) => (
                <div
                  key={l.id}
                  className="flex items-start justify-between gap-3 rounded-lg border border-border p-3 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {l.signature_valid ? (
                        <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-success" />
                      ) : (
                        <XCircle className="h-4 w-4 flex-shrink-0 text-destructive" />
                      )}
                      <span className="font-medium">{l.event}</span>
                      <span className="text-xs text-muted-foreground">
                        · {l.match_status ?? "—"}
                      </span>
                    </div>
                    {l.shopier_order_id && (
                      <div className="mt-0.5 font-mono text-xs text-muted-foreground">
                        Shopier #{l.shopier_order_id}
                      </div>
                    )}
                    {l.error && (
                      <div className="mt-1 text-xs text-destructive">⚠ {l.error}</div>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-xs text-muted-foreground">
                    {new Date(l.created_at).toLocaleString("tr-TR")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
